import { Alert, Box, Button, Container, Divider } from '@mui/material';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { Card } from 'components/Card';
import { CardContent } from 'components/Card/CardContent';
import { CardHeader } from 'components/Card/CardHeader';
import { LoadingState } from 'components/LoadingState';
import TextFieldWithValidationIcon from 'components/TextFieldWithValidationIcon/text-field-with-validation-icon';
import { NUMBERSONLY, YOUSEEACCOUNT } from 'utils/constants/errors';
import { inputValidation } from 'utils/errors';
import { getFromSessionStorage, setInSessionStorage } from 'utils/storage';
import { PublicCreditNoteHelpCards } from './HelpCards';
import { CreditNoteStatusCode, usePublicValidateCreditNote } from './api';

export const publicCreditNoteKey = 'public-credit-note-lookup:form';

type FormData = {
  accountNumber: string;
  creditNoteNumber: string;
  securityCode: string;
};

export const PublicCreditNoteLookup = () => {
  const [creditNoteStatusCode, setCreditNoteStatusCode] = useState<CreditNoteStatusCode>();
  const history = useHistory();

  const sessionStorageCreditNote: FormData = getFromSessionStorage(publicCreditNoteKey) || {};
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      accountNumber: sessionStorageCreditNote?.accountNumber || '',
      creditNoteNumber: sessionStorageCreditNote?.creditNoteNumber || '',
      securityCode: sessionStorageCreditNote?.securityCode || '',
    },
  });

  const { isFetching, isError, refetch } = usePublicValidateCreditNote(
    parseInt(watch('accountNumber')),
    parseInt(watch('creditNoteNumber')),
    parseInt(watch('securityCode')),
    { enabled: false, retry: false },
  );

  const onSubmit = async (form: FormData) => {
    const { status, data } = await refetch();
    const { statusCode, creditNote } = data || {};

    if (status !== 'success') {
      return;
    }

    if (statusCode === CreditNoteStatusCode.CreditNoteNotFound) {
      setCreditNoteStatusCode(statusCode);
      return;
    }

    if (creditNote) {
      const { accountNumber, creditNoteNumber, securityCode } = form;

      const params = new URLSearchParams({
        kontonummer: accountNumber,
        regningsnummer: creditNoteNumber,
        sikkerhedskode: securityCode,
      });

      setInSessionStorage(publicCreditNoteKey, form);
      history.push(`/udbetaling/se-kreditnota?${params.toString()}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Udbetaling af tilgodehavende - YouSee</title>
        <meta name='description' content='Her kan du få udbetalt tilgodehavende hos YouSee.' />
      </Helmet>
      <Container maxWidth='md' sx={{ mt: 5 }}>
        <Card sx={{ mt: 4 }}>
          <CardHeader title='Udbetaling af tilgodehavende' />
          <Divider />
          <CardContent>
            <p>
              Hvis du har modtaget en kreditnota, der viser, at du har penge til gode hos YouSee
              kan du her vælge hvor de skal udbetales til.
            </p>
            <p>Du finder de oplysninger du skal bruge på den kreditnota du har modtaget.</p>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                name='accountNumber'
                control={control}
                rules={{
                  required: 'Du skal udfylde feltet',
                  minLength: {
                    value: 9,
                    message: 'Dette felt skal være mindst 9 cifre langt',
                  },
                  maxLength: {
                    value: 9,
                    message: 'Dette felt skal maksimalt være 9 cifre langt',
                  },
                  validate: (value) =>
                    inputValidation({ value, errors: [NUMBERSONLY, YOUSEEACCOUNT] }) || true,
                }}
                render={({ field }) => (
                  <TextFieldWithValidationIcon
                    {...field}
                    sx={{
                      my: 3,
                      display: 'flex',
                      width: {
                        xs: '100%',
                        sm: '50%',
                      },
                    }}
                    label='Kontonummer'
                    type='tel'
                    error={!!errors?.accountNumber}
                    helperText={
                      errors?.accountNumber?.message ||
                      'Du kan finde dit YouSee kontonummer øverst i højre hjørne af din regning.'
                    }
                    required
                  />
                )}
              />
              <Controller
                name='creditNoteNumber'
                control={control}
                rules={{
                  required: 'Du skal udfylde feltet',
                  minLength: {
                    value: 1,
                    message: 'Dette felt skal være mindst 4 cifre langt',
                  },
                  maxLength: {
                    value: 4,
                    message: 'Dette felt skal maksimalt være 4 cifre langt',
                  },
                  validate: (value) => inputValidation({ value, errors: [NUMBERSONLY] }) || true,
                }}
                render={({ field }) => (
                  <TextFieldWithValidationIcon
                    {...field}
                    sx={{
                      my: 3,
                      display: 'flex',
                      width: {
                        xs: '100%',
                        sm: '50%',
                      },
                    }}
                    label='Kreditnota-nummer'
                    type='tel'
                    error={!!errors?.creditNoteNumber}
                    helperText={
                      errors?.creditNoteNumber?.message ||
                      'Du kan finde nummeret øverst i højre hjørne på din kreditnota.'
                    }
                    required
                  />
                )}
              />
              <Controller
                name='securityCode'
                control={control}
                rules={{
                  required: 'Du skal udfylde feltet',
                  minLength: {
                    value: 5,
                    message: 'Dette felt skal være mindst 5 cifre langt',
                  },
                  maxLength: {
                    value: 5,
                    message: 'Dette felt skal maksimalt være 5 cifre langt',
                  },
                  validate: (value) => inputValidation({ value, errors: [NUMBERSONLY] }) || true,
                }}
                render={({ field }) => (
                  <TextFieldWithValidationIcon
                    {...field}
                    sx={{
                      my: 3,
                      display: 'flex',
                      width: {
                        xs: '100%',
                        sm: '50%',
                      },
                    }}
                    label='Sikkerhedskode'
                    type='tel'
                    error={!!errors?.securityCode}
                    helperText={
                      errors?.securityCode?.message ||
                      'Sikkerhedskoden er den 5-cifrede kode du finder på din kreditnota.'
                    }
                    required
                  />
                )}
              />
              {creditNoteStatusCode === CreditNoteStatusCode.CreditNoteNotFound && (
                <Alert severity='warning' sx={{ mt: 3 }}>
                  Vi kan desværre ikke finde din kreditnota. Har du indtastet dit kontonummer,
                  kreditnota-nummer og sikkerhedskode korrekt?
                </Alert>
              )}
              {isError && (
                <Alert severity='error' sx={{ mt: 3 }}>
                  Der opstod en fejl
                </Alert>
              )}
              <Box sx={{ mt: 4 }}>
                <LoadingState loading={isFetching}>
                  <Button type='submit' variant='cta'>
                    Næste
                  </Button>
                </LoadingState>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
      <Container maxWidth='md' sx={{ mt: 5 }}>
        <PublicCreditNoteHelpCards />
      </Container>
    </>
  );
};

export default PublicCreditNoteLookup;
